import React, { useEffect, useRef } from 'react';
import '../Pages/styles.css'; // Import the prefixed CSS

// --- Import FontAwesomeIcon Component ---
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

// --- Import ALL Necessary Icons from Font Awesome (Updated for Feasibility Content) ---
import {
    // Core Concepts & General
    faCalculator,
    faChartPie,         // Feasibility / Analysis
    faMagnifyingGlassChart, // Market Analysis / Assessment
    faDollarSign,       // Financial / Budget / ROI / Funding
    faUsers,            // Community / Demographics / Patient Demand
    faBuilding,         // Facility / Infrastructure / Location
    faExclamationTriangle, // Risk Assessment / Mitigation
    faClipboardCheck,   // Compliance / Accreditation
    faBinoculars,       // Forecasting / Projections
    faLightbulb,        // Strategy / Opportunities / Decision-Making
    faMapMarkedAlt,     // Site Selection / Geographic Location
    faStore,            // Competition / Market Landscape
    // Scenarios
    faStethoscope,      // Add-on Services / Clinical
    faMicroscope,       // Advanced Equipment
    faTools,            // Renovation / Expansion
    faClinicMedical,    // Ancillary Services / Outpatient
    // faMapMarkedAlt already included
    // Methodology / Value Prop
    faDatabase,         // Data-Driven Insights
    faHandHoldingDollar,// Funding Acquisition
    faRoute,            // Market Entry Strategy
    faChartLine,        // Growth / Sustainability
    faUserTie,          // Expertise / Why Choose Us
    // Keep other potentially useful icons if needed
    // Icons likely unused from previous versions:
    /*
    faHardHat, faFileSignature, faHandshake, faKey, faTasks, faAward, faClock,
    faEye, faShoppingCart, faUserHardHat, faDraftingCompass, faRulerCombined, faPalette,
    faTree, faDirections, faLaptopCode, faTrophy, faWarehouse, faComments, faCog,
    faLeaf, faExpandArrowsAlt, faBiohazard
    */
} from '@fortawesome/free-solid-svg-icons'; // Using free solid icons


// --- Intersection Observer Hooks (No changes needed in hook logic) ---
const useIntersectionObserverTitle = (options) => {
    const containerRef = useRef(null);
    const [isVisible, setIsVisible] = React.useState(false);
    const callbackFunction = (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting) setIsVisible(true);
    };
    useEffect(() => {
        const observer = new IntersectionObserver(callbackFunction, options);
        const currentRef = containerRef.current;
        if (currentRef) observer.observe(currentRef);
        return () => { if (currentRef) observer.unobserve(currentRef); };
    }, [containerRef, options]);
    return [containerRef, isVisible];
};

const useIntersectionObserverElements = (options) => {
    useEffect(() => {
        const observerCallback = (entries, observer) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    const target = entry.target;
                    const parent = target.parentElement;
                    const siblings = parent ? Array.from(parent.children).filter(el => el.classList.contains('cliniqor-fade-in-up')) : [target];
                    const index = siblings.indexOf(target);
                    const staggerIndex = index >= 0 ? index : 0;

                    target.style.setProperty('--item-index', staggerIndex % 8); // Keep stagger logic
                    target.classList.add('cliniqor-visible'); // Use prefixed class
                    observer.unobserve(target);
                }
            });
        };
        const observer = new IntersectionObserver(observerCallback, options);
        const targets = document.querySelectorAll('.cliniqor-fade-in-up');
        targets.forEach(target => observer.observe(target));
        return () => targets.forEach(target => { if (target) observer.unobserve(target); });
    }, [options]);
};
// --- End Observer Hooks ---


function App() {

    // --- Observer Setup for Titles (Adjusted Refs) ---
    const titleObserverOptions = { threshold: 0.2 };
    const [title1Ref, title1Visible] = useIntersectionObserverTitle(titleObserverOptions); // Why Studies Matter
    const [title2Ref, title2Visible] = useIntersectionObserverTitle(titleObserverOptions); // Key Scenarios
    const [title3Ref, title3Visible] = useIntersectionObserverTitle(titleObserverOptions); // Cliniqor's Methodology
    const [title4Ref, title4Visible] = useIntersectionObserverTitle(titleObserverOptions); // Cliniqor's Value Proposition
    const [title5Ref, title5Visible] = useIntersectionObserverTitle(titleObserverOptions); // Why Choose Cliniqor?

    // --- Observer Setup for General Fade-In Elements ---
    useIntersectionObserverElements({ threshold: 0.1 });

    // Choose a relevant hero background image for feasibility/analysis
    const heroBgImage = 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1920&q=80'; // Analysis/charts image

  return(
    // Use prefixed base wrapper class
    <div className="cliniqor-app-wrapper">

      {/* --- HERO SECTION --- */}
      <header
        className="cliniqor-hero-section" // Prefixed
        style={{ backgroundImage: `url(${heroBgImage})` }}
      >
        <div className="cliniqor-container"> {/* Prefixed */}
          <div className="cliniqor-hero-content"> {/* Prefixed */}
            {/* --- NEW HERO CONTENT --- */}
            <h1>Market & Financial Feasibility Analysis</h1>
            <p className="cliniqor-sub-heading">
              Ensuring the Long-Term Success of Your Hospital Project with Cliniqor
            </p>
            <p className="cliniqor-intro-text">
              Is your hospital project truly viable? Cliniqor provides robust market and financial feasibility studies, the foundational pillars for informed decisions, effective resource allocation, and minimized risks in healthcare ventures.
            </p>
            {/* --- END NEW HERO CONTENT --- */}
          </div>
        </div>
      </header>

      <main>
        {/* --- WHY STUDIES MATTER --- */}
        <section className="cliniqor-section cliniqor-section-first cliniqor-section-bg-gradient cliniqor-intro-section">
          <div className="cliniqor-container">
            <div ref={title1Ref} className={`cliniqor-section-title cliniqor-fade-in-up ${title1Visible ? 'cliniqor-visible' : ''}`}>
              <h2>Why Do Market & Financial Feasibility Studies Matter?</h2>
              <p>The cornerstone of any successful healthcare project.</p>
            </div>
             <div className="cliniqor-intro-grid"> {/* Keeping grid structure */}
                 <div className="cliniqor-intro-text-content cliniqor-fade-in-up">
                     {/* --- NEW INTRO CONTENT --- */}
                     <p>A comprehensive study allows promoters to understand real community demand, accurately forecast investments, align financial projections, evaluate the competitive landscape, assess risks, and establish a framework to secure funding.</p>
                     <p>It ensures you build the right hospital that thrives in a dynamic market, whether launching new, expanding, or upgrading facilities.</p>
                     {/* --- END NEW INTRO CONTENT --- */}
                 </div>
                 {/* Optional: Could add a small list of the benefits here */}
             </div>
          </div>
        </section>

        {/* --- KEY SCENARIOS (Features Section - 5 items) --- */}
        <section className="cliniqor-section cliniqor-features-section cliniqor-section-bg-light">
          <div className="cliniqor-container">
            <div ref={title2Ref} className={`cliniqor-section-title cliniqor-fade-in-up ${title2Visible ? 'cliniqor-visible' : ''}`}>
              {/* --- NEW SECTION TITLE --- */}
              <h2>Key Scenarios Requiring Market & Financial Feasibility</h2>
              <p>Essential analysis during critical stages of the healthcare business lifecycle.</p>
              {/* --- END NEW SECTION TITLE --- */}
            </div>
            <div className="cliniqor-features-grid"> {/* Grid structure maintained (5 items) */}
              {/* --- NEW FEATURE CARDS --- */}
              <div className="cliniqor-feature-card cliniqor-fade-in-up">
                <div className="cliniqor-feature-card-content">
                    <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faStethoscope} /></div>
                    <h4>1. Introduction of Add-On Services</h4>
                    <p>Analyzing viability for new service lines (cardiology, oncology) based on demand and financials.</p>
                </div>
              </div>
              <div className="cliniqor-feature-card cliniqor-fade-in-up">
                 <div className="cliniqor-feature-card-content">
                    <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faMicroscope} /></div>
                    <h4>2. Acquisition of Advanced Medical Equipment</h4>
                    <p>Determining ROI for high-end tech (MRI, robotics) based on market need.</p>
                </div>
              </div>
              <div className="cliniqor-feature-card cliniqor-fade-in-up">
                 <div className="cliniqor-feature-card-content">
                    <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faTools} /></div>
                    <h4>3. Renovation or Expansion of Existing Facilities</h4>
                    <p>Assessing cost-effectiveness and sustainability for upgrades or expansions.</p>
                </div>
              </div>
               <div className="cliniqor-feature-card cliniqor-fade-in-up">
                 <div className="cliniqor-feature-card-content">
                    <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faClinicMedical} /></div>
                    <h4>4. Construction of New Ancillary Services</h4>
                    <p>Evaluating profitability and capacity for outpatient depts, diagnostic centers, etc.</p>
                </div>
              </div>
               <div className="cliniqor-feature-card cliniqor-fade-in-up">
                 <div className="cliniqor-feature-card-content">
                    <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faMapMarkedAlt} /></div>
                    <h4>5. Entry into New Geographic Locations</h4>
                    <p>Understanding local needs, regulations, and conditions before investment.</p>
                </div>
              </div>
              {/* --- END NEW FEATURE CARDS --- */}
            </div>
          </div>
        </section>

        {/* --- CLINIQOR'S METHODOLOGY (Detailed Planning Section - 2 main items) --- */}
        <section className="cliniqor-section cliniqor-section-bg-texture cliniqor-detailed-planning-section"> {/* Kept existing classes */}
          <div className="cliniqor-container">
             <div ref={title3Ref} className={`cliniqor-section-title cliniqor-fade-in-up ${title3Visible ? 'cliniqor-visible' : ''}`}>
              {/* --- NEW SECTION TITLE --- */}
              <h2>Cliniqor’s Proven Methodology for Feasibility Studies</h2>
               <p>A multi-step approach providing in-depth analysis and a clear roadmap.</p>
               {/* --- END NEW SECTION TITLE --- */}
            </div>
            {/* Using Planning Items for the two main methodology parts */}
            <div className="cliniqor-planning-items-wrapper">
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faMagnifyingGlassChart} /></div>
                    <div className="cliniqor-content"><h4>1. Comprehensive Market Feasibility Assessment</h4> <p>Evaluating healthcare demand (demographics, competition, gaps, trends) and regulatory/infrastructure landscape (licensing, utilities, access).</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faDollarSign} /></div>
                    <div className="cliniqor-content"><h4>2. Financial Feasibility Study</h4> <p>Analyzing capital requirements/investment, forecasting operating expenses (staff, O&M, marketing), projecting revenue/profitability, and assessing ROI/sustainability.</p> </div>
                 </div>
                 {/* Only 2 items representing the main sections */}
            </div>
          </div>
        </section>

        {/* --- VALUE PROPOSITION (Reusing Features Layout - 7 items) --- */}
        <section className="cliniqor-section cliniqor-features-section cliniqor-section-bg-light">
           <div className="cliniqor-container">
              <div ref={title4Ref} className={`cliniqor-section-title cliniqor-fade-in-up ${title4Visible ? 'cliniqor-visible' : ''}`}>
                 <h2>Cliniqor’s Value Proposition in Feasibility Studies</h2>
                 <p>Actionable insights enabling informed decisions for healthcare promoters and investors.</p>
              </div>
              {/* Adjusted grid for potentially 7 items */}
               <style>{`.cliniqor-features-grid { grid-template-columns: repeat(auto-fit, minmax(260px, 1fr)); } @media (min-width: 992px) { .cliniqor-features-grid { grid-template-columns: repeat(4, 1fr); } }`}</style> {/* Example: 4 cols on large screens */}
               <div className="cliniqor-features-grid">
                    {/* --- Cards for Value Proposition --- */}
                    <div className="cliniqor-feature-card cliniqor-fade-in-up">
                        <div className="cliniqor-feature-card-content">
                            <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faExclamationTriangle} /></div>
                            <h4>1. Identifying Risks & Mitigating Uncertainties</h4>
                            <p>Detecting market saturation, operational issues, expense risks, and policy impacts.</p>
                        </div>
                    </div>
                    <div className="cliniqor-feature-card cliniqor-fade-in-up">
                        <div className="cliniqor-feature-card-content">
                            <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faHandHoldingDollar} /></div>
                            <h4>2. Facilitating Funding Acquisition</h4>
                            <p>Providing comprehensive reports to build confidence for loans, private equity, or VC.</p>
                        </div>
                    </div>
                    <div className="cliniqor-feature-card cliniqor-fade-in-up">
                        <div className="cliniqor-feature-card-content">
                            <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faLightbulb} /></div>
                            <h4>3. Strategic Decision-Making</h4>
                            <p>Clear recommendations on size, services, and location aligning with needs and ROI.</p>
                        </div>
                    </div>
                     <div className="cliniqor-feature-card cliniqor-fade-in-up">
                        <div className="cliniqor-feature-card-content">
                            <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faCalculator} /></div> {/* Calculator for Planning */}
                            <h4>4. Comprehensive Financial Planning</h4>
                            <p>Detailed insights into cost management, revenue forecasting, and profitability.</p>
                        </div>
                    </div>
                    <div className="cliniqor-feature-card cliniqor-fade-in-up">
                        <div className="cliniqor-feature-card-content">
                            <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faRoute} /></div>
                            <h4>5. Market Entry Strategy</h4>
                            <p>Tailored strategies including competitor analysis, pricing, and patient acquisition plans.</p>
                        </div>
                    </div>
                     <div className="cliniqor-feature-card cliniqor-fade-in-up">
                        <div className="cliniqor-feature-card-content">
                            <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faChartLine} /></div>
                            <h4>6. Long-Term Sustainability & Growth</h4>
                            <p>Analyzing projected growth, market trends, and patient behavior for lasting success.</p>
                        </div>
                    </div>
                    <div className="cliniqor-feature-card cliniqor-fade-in-up">
                        <div className="cliniqor-feature-card-content">
                            <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faDatabase} /></div>
                            <h4>7. Data-Driven Insights</h4>
                            <p>Quantitative and qualitative insights for evidence-based decisions and reduced risk.</p>
                        </div>
                    </div>
               </div>
           </div>
        </section>

       {/* --- WHY CHOOSE CLINIQOR (Simple Text Section) --- */}
        <section className="cliniqor-section cliniqor-section-bg-dark"> {/* Using dark background */}
           <div className="cliniqor-container">
              <div ref={title5Ref} className={`cliniqor-section-title cliniqor-fade-in-up ${title5Visible ? 'cliniqor-visible' : ''}`}>
                 <h2 style={{ color: '#fff' }}>Why Choose Cliniqor for Your Feasibility Study?</h2>
              </div>
              <div className="cliniqor-text-content cliniqor-fade-in-up" style={{ textAlign: 'center', color: '#eee' }}>
                   <p>We are committed to providing accurate, reliable, and actionable market and financial feasibility studies. With over 12 years of successful collaborations, our expertise ensures your project is financially viable and strategically aligned with market demands.</p>
                   <p>Our comprehensive analysis covers every aspect, empowering you to mitigate risks and maximize profitability.</p>
                    {/* Optional Icon */}
                   {/* <div style={{ marginTop: '20px', fontSize: '2em', color: '#fff'}}>
                       <FontAwesomeIcon icon={faUserTie} />
                   </div> */}
              </div>
           </div>
        </section>

      </main>

      {/* --- FOOTER CTA --- */}
      <footer className="cliniqor-section cliniqor-section-narrow cliniqor-footer-cta">
        <div className="cliniqor-container cliniqor-fade-in-up">
          {/* --- UPDATED FOOTER CONTENT --- */}
          <h3>Ensure the Success of Your Healthcare Project</h3>
          <p>Contact Cliniqor today to learn more about how our market and financial feasibility services can help you make informed decisions, mitigate risks, and maximize profitability for your hospital project.</p>
          <a href="#contact" className="cliniqor-cta-button">Request a Feasibility Consultation</a>
          {/* --- END UPDATED FOOTER CONTENT --- */}
        </div>
      </footer>
    </div> // End cliniqor-app-wrapper
  );
}

export default App;